import React, { useState, useRef, useEffect } from "react";
import { IonButton, IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import { Helmet } from "react-helmet";

interface ImagePair {
    left_id: number;
    right_id: number;
    leftImage: string;
    rightImage: string;
}


const PhoneticWords: React.FC = () => {
    const [connections, setConnections] = useState<{ x1: number; y1: number; x2: number; y2: number; correct: boolean }[]>([]);
    const [selectedLeft, setSelectedLeft] = useState<{ id: number; x: number; y: number } | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const svgRef = useRef<SVGSVGElement | null>(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [totalQuestion, setTotalQuestion] = useState<any>([]);
    const [showImage, setShowImage] = useState(false);
    const [question, setQuestion] = useState('');
    const [questions, setQuestions] = useState<ImagePair[]>([]);
    const [height, setHeight] = useState('0px');
    const modal = useRef<HTMLIonModalElement>(null);

    // Get element position relative to .match-container
    const getRelativePosition = (element: HTMLDivElement) => {
        const container = containerRef.current; console.log(containerRef)
        if (!container) return { x: 0, y: 0 };

        const elementRect = element.getBoundingClientRect(); setHeight((container.scrollHeight) + 'px')
        const containerRect = container.getBoundingClientRect();

        const screenWidth = window.innerWidth;
        let elementX, elementY;
        if (screenWidth >= 1024) {
            elementX = element.offsetLeft + elementRect.width;
            elementY = element.offsetTop + elementRect.height / 2;
        }
        else {
            elementX = element.offsetLeft + elementRect.width / 2;
            elementY = element.offsetTop + elementRect.height / 2;
        }

        return {
            x: elementX,
            y: elementY
        }
    };

    // Handle left image click
    const handleLeftClick = (event: React.MouseEvent<HTMLDivElement>, id: number) => {
        const position = getRelativePosition(event.currentTarget); console.log('left position - ', position)
        setSelectedLeft({ id, x: position.x, y: position.y });
    };

    // Handle right image click and draw SVG line
    const handleRightClick = (event: React.MouseEvent<HTMLDivElement>, rightId: number) => {
        if (selectedLeft) {
            const position = getRelativePosition(event.currentTarget); console.log('right position - ', position)

            // Check if the connection is correct
            const isCorrect = questions.some(pair => selectedLeft.id === rightId);

            setConnections([...connections, { x1: selectedLeft.x, y1: selectedLeft.y, x2: position.x, y2: position.y, correct: isCorrect }]);
            setSelectedLeft(null);
        }
    };

    // Reset connections
    const resetConnections = () => {
        setConnections([]);
        setSelectedLeft(null);
    };

    function shuffleArray(array: any[]) {
        for (let i = array.length - 1; i > 0; i--) {
            // Generate a random index
            const randomIndex = Math.floor(Math.random() * (i + 1));
            // Swap elements
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    }

    const fetchQuestions = async () => {
        try {
            let data = [];

            if (process.env.REACT_APP_DEVELOPMENT !== 'WEB') {
                const response = await fetch(process.env.REACT_APP_API_URL + 'PhoneticWords.json');
                data = await response.json();
            } else {
                const module = await import('../../../datas/PhoneticWords.json');
                data = module.default;
            }

            const shuffledData = shuffleArray(data);
            setTotalQuestion(shuffledData);
            setQuestions(shuffledData[currentIndex].pairs);
            setQuestion(shuffledData[currentIndex].question);
        } catch (error) {
            console.error('Error loading data:', error);
        }
    };


    useEffect(() => {
        fetchQuestions();
        document.title = "Phonetic Words Worksheets for UKG | Learn CVC & Blending Sounds"; // Set page title
    }, []);

    const nextQuestion = () => {
        console.log('next question')
        resetConnections()
        setShowImage(true);
        setTimeout(() => {
            if (currentIndex != totalQuestion.length - 1) {
                setCurrentIndex(currentIndex + 1);
                console.log(totalQuestion[currentIndex + 1].pairs)
                setQuestions(totalQuestion[currentIndex + 1].pairs)
            }
            else {
                setCurrentIndex(0);
                fetchQuestions();
            }
            setShowImage(false);
        }, 3000);
    }

    return (
        <>
            <Helmet>
                <meta name="description" content='Discover engaging UKG phonetic word activities at Mama’s Guidance, designed to strengthen early reading skills through interactive worksheets and fun phonics-based learning for young children.' />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                        <IonModal
                            ref={modal}
                            trigger="open-modal"
                            initialBreakpoint={1}
                            breakpoints={[0, 0.5, 0.75, 1]}
                            handleBehavior="cycle"
                            backdropDismiss={false}  // ✅ Prevents click-to-close
                            className="full-page-modal"
                        >
                            <IonContent className="ion-padding">
                                <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                <h1>Phonetic Words</h1>

                                <p>
                                    Learning <strong>phonetic words</strong> is a vital part of early reading development. These specially designed worksheets for <strong>UKG (Upper Kindergarten)</strong> help children recognize and decode simple words using phonics. Through engaging visuals and sound-based activities, young learners can master basic <strong>CVC (consonant-vowel-consonant) words</strong>, beginning sounds, ending sounds, and blending.
                                </p>

                                <h2>🔤 What Are Phonetic Words?</h2>
                                <p>
                                    Phonetic words are those that can be sounded out easily. For example, the word <strong>“cat”</strong> can be broken into the sounds <strong>/c/ /a/ /t/</strong>. These worksheets help UKG students understand how letters and sounds work together to form words.
                                </p>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Recognize and pronounce basic CVC words (<strong>e.g. bat, dog, pin</strong>)</li>
                                    <li>Identify beginning, middle, and ending sounds</li>
                                    <li>Blend sounds to form complete words</li>
                                    <li>Improve reading fluency and vocabulary</li>
                                </ul>

                                <h2>📋 Activities Included in the Worksheets</h2>
                                <ul>
                                    <li>Circle the correct CVC word that matches the picture</li>
                                    <li>Match sounds with correct words</li>
                                    <li>Fill in the missing letter to complete a word</li>
                                    <li>Read and color the phonetic words</li>
                                    <li>Word search with phonetic patterns</li>
                                    <li>Blend sounds and choose the correct word</li>
                                </ul>

                                <h2>👩‍🏫 Tips for Parents & Teachers</h2>
                                <ul>
                                    <li>Sound out each letter slowly and clearly with your child</li>
                                    <li>Use flashcards and phonics charts for extra support</li>
                                    <li>Practice word families like <strong>“at”, “in”, “op”</strong> for better blending</li>
                                    <li>Encourage reading aloud every day</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/ukg/english-language/articles"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Articles</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/english-language/one-and-many"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>One and Many</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/english-language/rhyming-words"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Rhyming Words</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🌟 Mama’s Guidance Tip</h2>
                                <p>
                                    Make phonics fun! Use toys, pictures, and songs to help children connect sounds with objects and actions in daily life.
                                </p>

                                <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                            </IonContent>
                        </IonModal>
                        <article className="visually-hidden">
                            <h1>Phonetic Words</h1>

                            <p>
                                Learning <strong>phonetic words</strong> is a vital part of early reading development. These specially designed worksheets for <strong>UKG (Upper Kindergarten)</strong> help children recognize and decode simple words using phonics. Through engaging visuals and sound-based activities, young learners can master basic <strong>CVC (consonant-vowel-consonant) words</strong>, beginning sounds, ending sounds, and blending.
                            </p>

                            <h2>🔤 What Are Phonetic Words?</h2>
                            <p>
                                Phonetic words are those that can be sounded out easily. For example, the word <strong>“cat”</strong> can be broken into the sounds <strong>/c/ /a/ /t/</strong>. These worksheets help UKG students understand how letters and sounds work together to form words.
                            </p>

                            <h2>🎯 Learning Objectives</h2>
                            <ul>
                                <li>Recognize and pronounce basic CVC words (<strong>e.g. bat, dog, pin</strong>)</li>
                                <li>Identify beginning, middle, and ending sounds</li>
                                <li>Blend sounds to form complete words</li>
                                <li>Improve reading fluency and vocabulary</li>
                            </ul>

                            <h2>📋 Activities Included in the Worksheets</h2>
                            <ul>
                                <li>Circle the correct CVC word that matches the picture</li>
                                <li>Match sounds with correct words</li>
                                <li>Fill in the missing letter to complete a word</li>
                                <li>Read and color the phonetic words</li>
                                <li>Word search with phonetic patterns</li>
                                <li>Blend sounds and choose the correct word</li>
                            </ul>

                            <h2>👩‍🏫 Tips for Parents & Teachers</h2>
                            <ul>
                                <li>Sound out each letter slowly and clearly with your child</li>
                                <li>Use flashcards and phonics charts for extra support</li>
                                <li>Practice word families like <strong>“at”, “in”, “op”</strong> for better blending</li>
                                <li>Encourage reading aloud every day</li>
                            </ul>


                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/ukg/english-language/articles"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Articles</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/english-language/one-and-many"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>One and Many</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/english-language/rhyming-words"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Rhyming Words</IonLabel>
                                </IonItem>
                            </div>

                            <h2>🌟 Mama’s Guidance Tip</h2>
                            <p>
                                Make phonics fun! Use toys, pictures, and songs to help children connect sounds with objects and actions in daily life.
                            </p>
                        </article>
                        <div ref={containerRef} className="contentPart additionWord matchImage match-container" style={{ position: "relative", marginTop: "0%", paddingTop: "0%", height: "calc(-200px + 100vh)" }}>
                            {/* SVG for drawing lines */}
                            <svg ref={svgRef} className="connector-svg" height={height} style={{ position: "absolute", top: 0, left: 0, width: "100%", pointerEvents: "none", zIndex: 10 }}>
                                {connections.map((conn, index) => (
                                    <line
                                        key={index}
                                        x1={conn.x1}
                                        y1={conn.y1}
                                        x2={conn.x2}
                                        y2={conn.y2}
                                        stroke={conn.correct ? "green" : "red"} // Green if correct, Red if incorrect
                                        strokeWidth="3"
                                    />
                                ))}
                            </svg>


                            {/* <h6>Match Words to its Image</h6> */}
                            {/* Left & Right columns under SVG */}
                            <div className="mb-2 columns" style={{ display: "flex", justifyContent: "space-between", position: "relative", zIndex: 5, width: "100%", height: "100%" }}>
                                <div className="left-column">
                                    {questions.map((item) => (
                                        <div
                                            key={item.left_id}
                                            className={`image-box ${selectedLeft?.id === item.left_id ? "selected" : ""}`}
                                            onClick={(e) => handleLeftClick(e, item.left_id)}
                                            style={{ cursor: "pointer", padding: "10px" }}
                                        >
                                            {item.leftImage}
                                        </div>
                                    ))}
                                </div>

                                <div className="right-column">
                                    {questions.map((item) => (
                                        <div key={item.left_id} className="image-box" onClick={(e) => handleRightClick(e, item.right_id)} style={{ cursor: "pointer", padding: "10px" }}>
                                            <img className="" src={process.env.REACT_APP_API_URL + item.rightImage} alt="Right Option" style={{ width: "100px", height: "100px" }} />
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="mt-2 mb-4 text-center" style={{ display: 'flex', gap: '10px' }}>
                                <div className="optionButton background-blue" style={{ fontSize: '18px' }} onClick={resetConnections}>
                                    Reset
                                </div>
                                <div className="btn hero-btn" style={{ fontSize: '18px' }} onClick={() => nextQuestion()}>Next &gt;&gt;</div>
                            </div>

                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_LOADING_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default PhoneticWords;
